import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Loading } from '@appkit4/react-components';
import './OnlineFormEdit.scss';
import Breadcrumbs from '../../../components/shared/breadcrumbs/Breadcrumbs';
import OnlineFormFields from '../../../components/shared/OnlineFormFields/OnlineFormFields';
import PdfViewer from '../../../components/shared/pdfViewer/PdfViewer';
import {
    useGetV1ProcessGetOnlineFormsInProcessProcessId,
    useGetV1FillOnlineFormGetOnlineFormFields,
    usePostV1FillOnlineFormUpdateOnlineFormFields,
} from '../../../api/generated/endpoint';
import { UpdateExtractedFieldsDTO } from '../../../api/generated/models';
import { OnlineFormsResponse } from '../../../api/generated/models';
import { FieldUpdateItem } from '../../../api/generated/models';
import useGenericErrorToast from '../../../helpers/useGenericErrorToast';
import useToast from '../../../helpers/useToast';
import useLocale from '../../../locale/useLocale';

const OnlineFormEdit = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { documentId, onlineFormListingTab = 0 } = useParams();

    const isFillPdf = location.pathname.includes('fill-pdf');

    const [hasChanged, setHasChanged] = useState(false);

    const showToast = useToast();
    const { l } = useLocale();
    const showGenericErrorToast = useGenericErrorToast();

    const [breadcrumbItems, setBreadcrumbItems] = useState([
        {
            label: 'Process',
            href: '',
            processView: { listingTab: 0, selectedDocTypes: [] },
        },
        { label: 'Document' },
    ]);

    const activeTab = location.state?.activeTab || 0;
    const parentName = location.state?.parentName || '';

    const [processingData, setProcessingData] = useState<any[]>([]);

    const { data, error, isLoading } =
        useGetV1FillOnlineFormGetOnlineFormFields({
            DocumentId: Number(documentId),
        });

    useEffect(() => {
        if (error) {
            console.error('API Error:', error);
            showGenericErrorToast(error);
        }
    }, [error, showGenericErrorToast]);

    useEffect(() => {
        if (hasChanged == false) {
            if (data && Array.isArray(data.fields)) {
                setProcessingData(
                    data.fields?.sort((a: any, b: any) => {
                        const orderA = a.orderNumber || 0;
                        const orderB = b.orderNumber || 0;
                        if (orderA === 0 && orderB === 0) {
                            return a.fieldId - b.fieldId;
                        }
                        if (orderA === 0) return 1;
                        if (orderB === 0) return -1;
                        return orderA - orderB;
                    }) || []
                );
                setDocumentName(data.documentName || '');
                setProcessId(data.processId || null);
                setBreadcrumbItems([
                    {
                        label: data.processName || 'Process',
                        href: `/process/${data.processId}/uploaded`,
                        processView: {
                            listingTab: location.state?.listingTab || 0,
                            selectedDocTypes:
                                location.state?.selectedDocTypes || [],
                        },
                    },
                    { label: data.documentName || 'Document' },
                ]);
            }
        }
    }, [data, location.state]);
    const [processId, setProcessId] = useState<number | null>(1234);
    const [documentName, setDocumentName] = useState<string>('');

    const [documents, setDocuments] = useState<OnlineFormsResponse[]>([]);
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);

    const selectedDocTypes = location.state?.selectedDocTypes || [];

    const ValidationMapping: Record<string, number | number[]> = {
        'Awaiting Extraction': [2],
        'Awaiting Validation': [4, 9],
        'Validation Failed': [6],
        'Validation Low Confidence': [7],
        'Validation Passed': [8],
        'User Approved': [10],
        'PDF Generation Failed': [11],
        'PDF Generated': [12],
        'System Error': [1, 3, 5],
    };

    const getStatusByNumber = (num: number) => {
        for (const [key, value] of Object.entries(ValidationMapping)) {
            if (Array.isArray(value) ? value.includes(num) : value === num) {
                return key;
            }
        }
        return undefined;
    };

    const filterActionData = (data: OnlineFormsResponse[]) => {
        return data.filter((row) => {
            const status = getStatusByNumber(row.processStatus || 0);
            return (
                status === 'System Error' ||
                status === 'Validation Low Confidence' ||
                status === 'Validation Failed'
            );
        });
    };

    const filterByDocType = (data: OnlineFormsResponse[]) => {
        if (selectedDocTypes.length === 0) return data;
        return data.filter((item) =>
            selectedDocTypes.includes(item.documentType || '')
        );
    };

    const getFilteredDocuments = (data: OnlineFormsResponse[]) => {
        const tabFiltered =
            Number(onlineFormListingTab) === 1 ? filterActionData(data) : data;
        return filterByDocType(tabFiltered);
    };

    const {
        data: processDocuments,
        error: processDocumentsError,
        refetch: refetchProcessDocuments,
    } = useGetV1ProcessGetOnlineFormsInProcessProcessId(
        Number(processId),
        undefined,
        {
            query: {
                enabled: processId !== null,
            },
        }
    );

    const updateFields = usePostV1FillOnlineFormUpdateOnlineFormFields();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isProceeding, setIsProceeding] = useState(false);

    useEffect(() => {
        if (processDocumentsError) {
            console.error('API Error:', processDocumentsError);
            showGenericErrorToast(processDocumentsError);
        }
    }, [processDocumentsError, showGenericErrorToast]);

    useEffect(() => {
        if (processDocuments && Array.isArray(processDocuments.onlineForms)) {
            const filteredDocuments = processDocuments.onlineForms.filter(
                (doc) =>
                    doc.processStatus !== undefined &&
                    ![1, 2, 3, 4, 5, 9].includes(doc.processStatus)
            );

            const finalFilteredDocuments =
                getFilteredDocuments(filteredDocuments);
            setDocuments(finalFilteredDocuments);

            const index = finalFilteredDocuments.findIndex(
                (doc) => doc.documentId === Number(documentId)
            );
            setCurrentDocumentIndex(index !== -1 ? index : 0);
        }
    }, [processDocuments, documentId, onlineFormListingTab, selectedDocTypes]);

    const handleFieldUpdate = (
        field: string,
        value: any,
        checkboxSelectedValues: any
    ) => {
        setHasChanged(true);
        setProcessingData((prev) => {
            if (!prev) return prev;
            if (checkboxSelectedValues) {
                return [
                    ...prev.map((item) =>
                        item.fieldName === field
                            ? {
                                  ...item,
                                  checkboxSelectedValues,
                              }
                            : item
                    ),
                ];
            }
            if (field === 'date' || field === 'documentType') {
                return [
                    ...prev.map((item) =>
                        item.fieldName === field
                            ? {
                                  ...item,
                                  value: Array.isArray(value)
                                      ? value
                                      : [
                                            ...(Array.isArray(item.value)
                                                ? item.value
                                                : []),
                                            value,
                                        ],
                              }
                            : item
                    ),
                ];
            }
            return [
                ...prev.map((item) =>
                    item.fieldName === field ? { ...item, value } : item
                ),
            ];
        });
    };

    const getNextDocument = async () => {
        if (documents.length === 0) return;
        const nextIndex = (currentDocumentIndex + 1) % documents.length;
        const nextDocument = documents[nextIndex];

        navigate(
            `/process/${processId}/online-form/${nextDocument.documentId}`,
            {
                state: {
                    documentId: nextDocument.documentId,
                    parentLocation: `/process/${processId}/uploaded`,
                    fileName: nextDocument.documentId,
                    parentName: parentName,
                    activeTab: activeTab,
                    selectedDocTypes: selectedDocTypes,
                    onlineFormListingTab: onlineFormListingTab,
                },
            }
        );
    };

    const handleProceed = async () => {
        setIsSubmitting(true);
        const updatedFields: FieldUpdateItem[] = (processingData || []).map(
            (item) => ({
                fieldId: item.fieldId,
                newValue:
                    item.checkboxSelectedValues &&
                    item.checkboxSelectedValues.length > 0
                        ? item.checkboxSelectedValues.join(', ')
                        : Array.isArray(item.value)
                        ? item.value.join(', ')
                        : item.value,
                selectedCheckboxOptions: item.checkboxSelectedValues,
            })
        );
        const updatePayload: UpdateExtractedFieldsDTO = {
            documentId: Number(documentId),
            fieldUpdates: updatedFields,
        };
        try {
            await updateFields.mutateAsync({ data: updatePayload });
            showToast(l('_success'), l('_fieldsUpdated'), 'success');
            setHasChanged(false);
            if (processId) {
                navigate(`/process/${processId}/uploaded`, {
                    state: {
                        onlineFormListingTab: onlineFormListingTab,
                        selectedDocTypes: selectedDocTypes,
                    },
                });
            }
        } catch (error: any) {
            showGenericErrorToast(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNext = async () => {
        setIsProceeding(true);
        const updatedFields: FieldUpdateItem[] = (processingData || []).map(
            (item) => ({
                fieldId: item.fieldId,
                newValue:
                    item.checkboxSelectedValues &&
                    item.checkboxSelectedValues.length > 0
                        ? item.checkboxSelectedValues.join(', ')
                        : Array.isArray(item.value)
                        ? item.value.join(', ')
                        : item.value,
                selectedCheckboxOptions: item.checkboxSelectedValues,
            })
        );
        const updatePayload: UpdateExtractedFieldsDTO = {
            documentId: Number(documentId),
            fieldUpdates: updatedFields,
        };
        try {
            await updateFields.mutateAsync({ data: updatePayload });
            showToast(l('_success'), l('_fieldsUpdated'), 'success');
            setHasChanged(false);
            await refetchProcessDocuments();
            await new Promise((resolve) => setTimeout(resolve, 3000));
            getNextDocument();
        } catch (error: any) {
            showGenericErrorToast(error);
        } finally {
            setIsProceeding(false);
        }
    };

    return (
        <div className="processing-edit-container">
            <Breadcrumbs items={breadcrumbItems} />
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <h2 className="ap-typography-heading-l title">
                    {documentName}
                </h2>
                {data?.onlineFormAccessCode && (
                    <div style={{ fontSize: '16px', fontWeight: '500' }}>
                        Access Code:{' '}
                        <span
                            style={{
                                fontFamily: 'monospace',
                                fontSize: '18px',
                            }}
                        >
                            {data.onlineFormAccessCode}
                        </span>
                    </div>
                )}
            </div>
            {isLoading ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '400px',
                    }}
                >
                    <Loading
                        loadingType="circular"
                        size="large"
                        text="Loading form fields..."
                    />
                </div>
            ) : data?.sasUrl ? (
                <div className="processing-edit">
                    <div className="pdf-section">
                        <PdfViewer currentPage={1} fileUrl={data.sasUrl} />
                    </div>
                    <div className="edit-section">
                        <OnlineFormFields
                            onUpdate={handleFieldUpdate}
                            onProceed={handleProceed}
                            onNext={handleNext}
                            fields={processingData}
                            isSubmitting={isSubmitting}
                            isProceeding={isProceeding}
                            hasNextDocument={documents.length > 1}
                            hideSectionNames={isFillPdf}
                        />
                    </div>
                </div>
            ) : (
                <div className="edit-section full-width">
                    <OnlineFormFields
                        onUpdate={handleFieldUpdate}
                        onProceed={handleProceed}
                        onNext={handleNext}
                        fields={processingData}
                        isSubmitting={isSubmitting}
                        isProceeding={isProceeding}
                        hasNextDocument={documents.length > 1}
                        hideSectionNames={isFillPdf}
                    />
                </div>
            )}
        </div>
    );
};

export default OnlineFormEdit;
